/**
 * Настройки, которые приходят В РАНТАЙМЕ, а не запекаются в бандл.
 *
 * Всё остальное живёт в `env.ts` и подставляется Vite при сборке. Здесь
 * — то, что различается между площадками: номер счётчика Метрики.
 *
 * Почему не `VITE_*`, как всё прочее. `import.meta.env` подставляется
 * НА СБОРКЕ, то есть образ стал бы привязан к площадке: под стенд
 * и прод пришлось бы собирать разные. Сейчас у фронта нет ни одного
 * build-аргумента, и благодаря этому проверенный на стенде образ — тот
 * же самый, что уедет в прод. Терять это свойство ради двух строк
 * конфигурации неразумно: ошибка «выложили образ стенда в прод» ничем
 * себя не проявляет, кроме статистики, которая молча пишется не туда.
 *
 * Файл отдаёт nginx контейнера — он генерирует его при старте из
 * переменных окружения (`apps/frontend/docker/20-runtime-config.sh`).
 * Нет файла или он пустой — приложение работает полностью, просто без
 * аналитики. Ровно так же ведёт себя сборка без ключа.
 */
export interface RuntimeConfig {
  metrikaCounterId: number | null;
}

const EMPTY: RuntimeConfig = { metrikaCounterId: null };

/**
 * Пустая строка — это отсутствие значения, а не значение.
 *
 * Шаблон в контейнере всегда пишет ключ: на площадке без аналитики он
 * выходит пустым. Без этой нормализации счётчик инициализировался бы
 * номером 0 — `Number('')` даёт ноль — и молча слал бы события в никуда.
 */
function counterOrNull(value: unknown): number | null {
  const raw =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value.trim())
        : Number.NaN;
  return Number.isInteger(raw) && raw > 0 ? raw : null;
}

let loaded: Promise<RuntimeConfig> | null = null;

/**
 * Читается один раз за жизнь страницы: повторные вызовы получают тот же
 * промис. Отказ — не ошибка приложения: аналитика не тот повод, чтобы
 * ронять экран, поэтому любая неудача даёт пустой конфиг.
 */
export function loadRuntimeConfig(): Promise<RuntimeConfig> {
  loaded ??= fetch('/config.json', { cache: 'no-store' })
    .then((res): Promise<Record<string, unknown>> =>
      res.ok
        ? (res.json() as Promise<Record<string, unknown>>)
        : Promise.resolve({}),
    )
    .then((raw) => ({ metrikaCounterId: counterOrNull(raw.metrikaCounterId) }))
    .catch(() => EMPTY);

  return loaded;
}
